/**
 * DETAIL WORK, REPORTED — AND NOT SCORED.
 *
 * The Detail Specialist lens needed something true to show. What exists is
 * the frozen attribution: `performed_work_family = 'DETAIL'` on
 * `ticket_job_performer_evidence`, captured when the minutes were allocated.
 * That supports exactly three honest claims — what was done, how much of it,
 * and over what period.
 *
 * It does NOT support a detail proficiency, a detail skill tier, a detail
 * comeback rate or a detail efficiency percentage, because nothing in the
 * product measures detail QUALITY yet. CORE-E learns mechanical categories and
 * explicitly excludes the DETAIL family at source; CORE-G's efficiency is
 * computed against mechanical book times. Manufacturing a number here to make
 * the lens look as full as the Technician lens would put a fabricated
 * competence on a real person's professional record, which is worse than a
 * short page.
 *
 * So the section is deliberately small, and the "what is not here" line is
 * part of the content rather than an apology for it.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import type { PassportDetailWorkEvidence as DetailEvidence } from '@/hooks/passport/usePassportWorkLenses';

export interface PassportDetailWorkEvidenceProps {
  evidence: DetailEvidence | null;
  /** The read failed. "Could not load" is not the same answer as "none". */
  isError?: boolean;
  isLoading?: boolean;
}

const when = (iso: string | null): string | null => {
  if (!iso) return null;
  try {
    return format(parseISO(iso), 'MMM yyyy');
  } catch {
    return null;
  }
};

export function PassportDetailWorkEvidence({
  evidence,
  isError,
  isLoading,
}: PassportDetailWorkEvidenceProps) {
  const first = when(evidence?.firstWorkedAt ?? null);
  const last = when(evidence?.lastWorkedAt ?? null);

  return (
    <Card data-testid="passport-detail-work-evidence">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sparkles className="h-5 w-5 text-primary" />
          Detail Work
        </CardTitle>
        <CardDescription>
          Recorded detail work, from the role captured when each job&apos;s minutes
          were allocated.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading detail work…</p>
        ) : isError ? (
          <p className="text-sm text-muted-foreground" data-testid="detail-work-error">
            Detail work could not be loaded. This does not mean there is none.
          </p>
        ) : !evidence ? (
          <p className="text-sm text-muted-foreground" data-testid="detail-work-empty">
            No detail work recorded yet.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-2xl font-semibold">{evidence.jobCount}</div>
                <div className="text-xs text-muted-foreground">
                  detail job{evidence.jobCount === 1 ? '' : 's'}
                </div>
              </div>
              <div>
                <div className="text-2xl font-semibold">{evidence.ticketCount}</div>
                <div className="text-xs text-muted-foreground">
                  vehicle{evidence.ticketCount === 1 ? '' : 's'}
                </div>
              </div>
              <div>
                <div className="text-2xl font-semibold">
                  {Math.round(evidence.minutes / 60)}
                </div>
                <div className="text-xs text-muted-foreground">recorded hours</div>
              </div>
            </div>
            {(first || last) && (
              <p className="mt-3 text-xs text-muted-foreground">
                {first && last && first !== last
                  ? `${first} – ${last}`
                  : (last ?? first)}
              </p>
            )}
            {evidence.truncated && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="detail-work-truncated">
                Showing the most recent records — the totals above are at least
                this much.
              </p>
            )}
          </>
        )}
        <p className="mt-4 border-t pt-3 text-xs text-muted-foreground">
          OptiBay does not score detail quality yet, so there is no detail
          proficiency or efficiency figure here. Mechanical proficiency,
          diagnostic competency and comeback rates are not shown under this lens
          because detail work is not evidence about them.
        </p>
      </CardContent>
    </Card>
  );
}
